import { observer } from "mobx-react-lite"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Menu from "@mui/material/Menu"
import useStore from "reusable/mobx-store-utils"
import MoreMenuStore from "./MoreMenuStore"
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material/SvgIcon";
import { SxProps } from "@mui/material/styles"
import { forwardRef, ReactElement } from "react"
import IconButton from "@mui/material/IconButton"
import { themeConfig } from '@gap/shared/theme'
import Tooltip from "@mui/material/Tooltip"
import MoreVert from "@mui/icons-material/MoreVert"

type MuiIcon = OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string; }

export type MoreMenuOption = {
   callback?: (id?: string | undefined) => Promise<void> | void,
   icon?: MuiIcon
   iconColor?: string
   sx?: SxProps
   disabled?: boolean
   tooltip?: string
   key?: string
   preventMenuClose?: boolean
} & ({
   title: string
} | {
   title: React.ReactNode
   key: string
})

interface MoreMenuProps {
   icon?: ReactElement<any, any>
   width?: string
   /** Optional ID to be sent through in the callback */
   id?: string
   options: MoreMenuOption[]
}

const MoreMenu = forwardRef(function MoreMenu(props: MoreMenuProps, ref: any) {
   const anchor: HTMLElement | null = null

   const localStore = useStore(sp => ({
      clickHandler: async (clickedIndex: string | undefined) => {
         if (!clickedIndex) {
            return
         }
         const option = sp.options[Number(clickedIndex)]
         if (option && !option.disabled) {
            await option.callback?.(sp.id)
         }
      }
   }), { options: props.options, id: props.id })

   const moreMenuStore = useStore(sp => new MoreMenuStore(sp), { anchor, callback: localStore.clickHandler })

   return (
      <Box sx={{ cursor: 'pointer' }}>
         <Box onClick={moreMenuStore.openMenu} sx={{ textAlign: 'center' }}>
            <IconButton sx={{ padding: 0 }} ref={ref}>
               {props.icon || <MoreVert />}
            </IconButton>
         </Box>

         <Menu
            anchorEl={moreMenuStore.sp.anchor}
            open={moreMenuStore.open}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            onClose={moreMenuStore.closeMenu}
         >
            <Box sx={{ width: props.width || '200px', cursor: 'pointer' }}>
               {
                  props.options.map((option, index) => {
                     // Use index for data-id, as the title could change (ex. G&A and mark and completed changes)
                     return (
                        <Box
                           sx={{
                              pb: 1,
                              pl: 2,
                              pt: 1,
                              ':hover': { bgcolor: 'action.hover', borderRadius: '3px' },
                              ...(option.disabled ? { cursor: 'default' } : {}),
                              ...option.sx
                           }}
                           data-id={index}
                           key={typeof option.title === 'string' ? option.title : option.key}
                           onClick={moreMenuStore.closeMenu}
                           {...option.preventMenuClose ? {
                              'data-prevent-menu-close': true,
                           } : {}}
                        >
                           <Tooltip title={option.tooltip}>
                              <Box>
                                 {option.icon && <option.icon fontSize="small" sx={{ float: 'left', mr: 2, ...(option.disabled ? { color: themeConfig.primary[200] } : { color: option.iconColor || themeConfig.primary[800] }) }} />}
                                 {typeof option.title === 'string' && <Typography variant="body2" sx={option.disabled ? { color: themeConfig.primary[400] } : {}}>{option.title}</Typography>}
                                 {typeof option.title !== 'string' && option.title}
                              </Box>
                           </Tooltip>
                        </Box>
                     );
                  })
               }
            </Box>
         </Menu>
      </Box >
   );
})

export default observer(MoreMenu)
